import React from 'react';
import {
    Button, FormGroup, FormText, Progress,
} from 'reactstrap';
import { debounce } from 'lodash-es';
import moment from 'moment';
import AsyncSelect from 'react-select/async';
import { faMoneyCheck, faUndo, faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from '@s20/components/common/Link';
import { httpClient } from '@s20/services/httpClient';
import CommonContext, { ApiRoutes, MomentDateFormat, AppNavPaths } from '../Common';
import {
    createDataSource, createGridOptions, DataGrid, IconCellRenderer,
    LinkCellRenderer, TextFilterDefaults, DateFilterDefaults, VariableLinkCellRenderer,
    TimesheetNumberCellRenderer, FlagIconCellRenderer, EmployeeAccountStatusRenderer,
} from '../common/dataGrid/DataGrid';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import DataGridSelectFilter from '../common/dataGrid/DataGridSelectFilter';
import DataGridSelectFloatingFilter from '../common/dataGrid/DataGridSelectFloatingFilter';
import DataGridToolbar from '../common/dataGrid/DataGridToolbar';
import {
    onFieldChange, PageHeading, PageWrap, toasty, FormLabel, onReactSelectChanged,
    Textarea, Input,
} from '../common/forms/FormElements';
import { NumericInput } from '../common/forms/NumericInput';
import { NotAuthorizedPage } from '../status/StatusCodes';
import { handleFormSaveError } from '../common/forms/ValidationError';
import { PayrollFlags } from '../payroll/Payroll';
import { ChargeTypeUnits } from '../chargeType/ChargeType';
import TimePicker from '../common/forms/TimePicker';
import { PayPeriodTypes } from '../organization/Organization';
import { ModalForm } from '../common/forms/ModalForm';
import { Modal } from '../common/Modal';
import Chip from '../common/forms/Chip';
import PayrollCalculationSlideout from './PayrollCalculationSlideout';

const getTotalHours = (timesheet, start, end) => {
    // Calculate the duration between Start and End.  Factor in if this enters into the next day.
    // Use the job start date/time as opposed to moment defaulting to the current day when getting duration.
    if (start && end) {
        const time_format = 'HH:mm';

        const mStartTime = moment(start, time_format); const
            mEndTime = moment(end, time_format);
        if (mEndTime.isBefore(mStartTime)) {
            mEndTime.add(1, 'day');
        }

        const dur = moment.duration(mEndTime.diff(mStartTime));
        const value = dur.asHours().toFixed(2);

        return value;
    }
};

const transformRowData = (data) => {
    data.rows.forEach((row) => {
        // Placeholder column so we can have it be the last checkable thing on the right.
        // Otherwise, we could set checkboxSelection = true for an existing column.
        row.selectionPlaceholder = '';
    });
    return { ...data };
};

class PayrollManagementIndex extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.payrollCalculationRef = React.createRef();

        this.state = {
            loading: true,
            rowData: [],
            rowsSelected: [],
            selectedDispatchLocation: null,
            selectedWeekOf: '',
            isProcessing: false,
            showRejectPayrollModal: false,
            isRejectingPayroll: false,
            adminRejecting: false,
            selectedRowRejectionNotes: '',
            showAddChargeModal: false,
            isAddingCharge: false,
            payrollChargeDate: null,
            payrollCharges: null,
            selectedPayrollChargeType: null,
            selectedPayrollDispatchLocation: null,
            dispatchLocationEmployees: null,
            payrollChargeValue: 0,
        };

        this.onRowSelected = this.onRowSelected.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onSelectChanged = this.onSelectChanged.bind(this);
        this.onDispatchLocationChanged = this.onDispatchLocationChanged.bind(this);
    }

    onSelectChanged = onReactSelectChanged;

    componentDidMount() {
        return this.populateState();
    }

    componentDidUpdate(prevProps, prevState) {
        const { blockedTimesheets, blockedMessage } = this.state;

        if (blockedTimesheets !== prevState.blockedTimesheets && blockedTimesheets?.length > 0) {
            Modal.show({
                title: 'Payroll Processing Blocked',
                SaveButtonProps: {
                    hidden: true,
                },
                onClose: () => this.setState({ isProcessing: false, blockedTimesheets: null, blockedMessage: null }),
                body: () => (
                    <div className="mb-4">
                        <div
                            style={{ marginBottom: '20px' }}
                            className="form-text text-danger"
                        >
                            <strong>{blockedMessage}</strong>
                        </div>
                        {blockedTimesheets.map((ts) => (
                            <Chip
                                key={`${ts.id}`}
                                component={Link}
                                to={`${AppNavPaths.TimesheetManagement}?t=${ts.id}`}
                                target="_blank"
                                className="mr-2"
                                text={(
                                    <span>
                                        {ts.timesheetNumber}
                                        <FontAwesomeIcon
                                            icon={faArrowUpRightFromSquare}
                                            className="ms-3"
                                        />
                                    </span>
                                )}
                            />
                        ))}
                    </div>
                ),
            });
        }
    }

    onAddPayrollCharge = async () => {
        const {
            selectedPayrollChargeType,
            selectedPayrollEmployee,
            selectedPayrollDispatchLocation,
            payrollChargeDate,
            payrollChargeValue,
        } = this.state;

        if (!selectedPayrollChargeType
            || !selectedPayrollEmployee
            || !selectedPayrollDispatchLocation
            || !payrollChargeDate
            || payrollChargeValue == 0) {
            toasty.error('Error: All fields must have a value');
            return;
        }

        this.setState({ isAddingCharge: true });

        try {
            const data = {
                dispatchLocationId: selectedPayrollDispatchLocation.value,
                chargeTypeId: selectedPayrollChargeType.chargeTypeId,
                contractId: selectedPayrollChargeType.contractId,
                employeeId: selectedPayrollEmployee.id,
                date: payrollChargeDate,
                value: payrollChargeValue,
            };

            await httpClient.post(ApiRoutes.payrollManagement.addPayrollCharge(), data);

            toasty.success('Charge Added.');
            this.state.gridOptions.refresh();
        } catch (e) {
            if (e?.handledError?.message) {
                toasty.error(`There was an issue adding the charge record. ${e.handledError.message}`);
            } else {
                toasty.error('There was an issue adding the charge record.  Please try your request again or contact support for assistance.');
            }
        } finally {
            this.setState({ isAddingCharge: false, showAddPayrollChargeModal: false });
        }

        return;
    };

    onAddCharge = async () => {
        const {
            selectedTimesheet,
            selectedChargeType,
            selectedResource,
            chargeStartTime,
            chargeEndTime,
            totalChargeTime,
            chargeNotes,
        } = this.state;

        if (selectedTimesheet == null
            || selectedChargeType == null
            || selectedResource == null
            || totalChargeTime == 0
            || !chargeNotes || chargeNotes.length == 0) {
            toasty.error('Error: All fields must have a value');
            return;
        }

        this.setState({ isAddingCharge: true });

        try {
            const data = {
                timesheetId: selectedTimesheet.value,
                chargeTypeId: selectedChargeType.value,
                chargeTypeUnitsId: selectedChargeType.unitsId,
                resourceId: selectedResource.value,
                start: chargeStartTime,
                end: chargeEndTime,
                value: totalChargeTime,
                notes: chargeNotes,
            };

            await httpClient.post(ApiRoutes.payrollManagement.add(), data);

            toasty.success('Charge Added.');
            this.state.gridOptions.refresh();
        } catch {
            toasty.error('There was an issue adding the charge record.  Please try your request again or contact support for assistance.');
        } finally {
            this.setState({ isAddingCharge: false, showAddChargeModal: false });
        }
    };

    onApproveButtonClick = async () => {
        this.setState({ isApproving: true });

        const { rowsSelected } = this.state;

        // call accept
        if (rowsSelected) {
            const model = rowsSelected.map((x) => x.id);
            const response = await httpClient.post(ApiRoutes.payrollManagement.approve(), model);

            if (response) {
                toasty.error(`Error approving payroll records: ${response}`);
            } else {
                toasty.success('Payroll records approved');
            }
        }

        this.state.gridOptions.refresh();

        this.setState({ rowsSelected: [], isApproving: false });
    };

    onChange = onFieldChange;

    onChargeNotesChanged = (ev) => {
        const val = ev.target.value;
        this.setState({ chargeNotes: val });
    };

    onChargeValueChanged = (ev) => {
        const val = ev.target.value;
        this.setState({ totalChargeTime: val });
    };

    onPayrollCalculationClosed = async () => {
        await this.context.setFormOpened(false);
        this.setState({ selectedRow: null });
        this.state.gridOptions.refresh();
    };

    async onProcessClick(values) {
        const { selectedWeekOf, selectedDispatchLocation } = values;

        if (!selectedWeekOf || !selectedDispatchLocation) return;

        this.setState({ isProcessing: true });

        const model = {
            weekOf: new Date(selectedWeekOf),
            dispatchLocationId: selectedDispatchLocation,
        };
        try {
            const response = await httpClient.post(ApiRoutes.payrollManagement.process(), model);
            if (response) {
                if (response.data?.length > 0) {
                    this.setState({ blockedTimesheets: response.data, blockedMessage: response.message });
                } else if (response.message) {
                    toasty.error(response.message);
                    this.state.gridOptions.refresh();
                    this.setState({ rowsSelected: [], isProcessing: false });
                } else {
                    toasty.success('Processing complete');
                    this.state.gridOptions.refresh();
                    this.setState({ rowsSelected: [], isProcessing: false });
                }
            }
        } catch {
            toasty.error('Error processing payroll records');
        }
    }

    // https://stackoverflow.com/questions/44263350/count-number-of-selected-rows-in-ag-grid
    // Possibly use lodash dequeue?
    onRowSelected(e) {
        const rs = e.api.getSelectedRows();

        this.setState({
            rowsSelected: rs,
        });
    }

    onSelectedChargeTypeChanged = async (selection) => {
        const { selectedTimesheet } = this.state;
        await this.loadChargeResourcesList(selectedTimesheet, selection);
    };

    onSelectedDispatchLocationChanged = (selection) => {
        const loc = { ...selection };
        this.setState({ selectedDispatchLocation: loc.value });
    };

    onSelectedResourceChanged = (selection) => {
        this.setState({ selectedResource: selection });
    };

    onSelectedTimesheetChanged = async (selection) => {
        const paymentChargeTypes = await httpClient.js(ApiRoutes.typeAheads.payrollChargeTypes(selection.value));
        this.setState({
            selectedTimesheet: selection,
            paymentChargeTypes,
            selectedChargeType: null,
            selectedResource: null,
            billingChargeResources: [],
        });
    };

    onTimeEntryEndChanged = (ev) => {
        const val = ev.target.value;
        const timeValue = ev.target.value ? moment(val, ['h:mm A']).format('HH:mm') : '';

        const { selectedTimesheet, chargeStartTime } = { ...this.state };
        const total = getTotalHours(selectedTimesheet, chargeStartTime, timeValue);

        this.setState({ totalChargeTime: total, chargeEndTime: timeValue });
    };

    onTimeEntryStartChanged = (ev) => {
        const val = ev.target.value;
        const timeValue = ev.target.value ? moment(val, ['h:mm A']).format('HH:mm') : '';

        const { selectedTimesheet, chargeEndTime } = { ...this.state };
        const total = getTotalHours(selectedTimesheet, timeValue, chargeEndTime);

        this.setState({ totalChargeTime: total, chargeStartTime: timeValue });
    };

    getColumnDefs({
        dispatchLocations, payrollStatuses, billingStatuses, flags, yesNo,
    }) {
        // https://www.ag-grid.com/documentation/javascript/column-definitions/

        const dispatchFilterParams = {
            colDef: 'Payroll.DispatchLocationName',
            column: 'Payroll.DispatchLocationName',
            suppressFilterButton: true,
            labelText: 'Filter by Dispatch',
            options: dispatchLocations,
            optionsLabel: 'label',
            optionsValue: 'value',
        };
        const payrollStatusFilterParams = {
            suppressFilterButton: true,
            options: payrollStatuses,
            optionsLabel: 'label',
            optionsValue: 'value',
        };
        const billingStatusFilterParams = {
            suppressFilterButton: true,
            options: billingStatuses,
            optionsLabel: 'label',
            optionsValue: 'value',
        };
        const flagsFilterParams = {
            suppressFilterButton: true,
            options: flags,
            optionsLabel: 'label',
            optionsValue: 'value',
        };
        const yesNoFilterParams = {
            suppressFilterButton: true,
            options: yesNo,
            optionsLabel: 'label',
            optionsValue: 'value',
        };

        const defs = [
            {
                headerName: '',
                valueGetter: 'node.id',
                sortable: false,
                hide: true,
                flex: 1,
                maxWidth: 35,
                cellRenderer: this.indexCellRenderer,
            },
            {
                colId: 'TimesheetNumber',
                headerName: 'Timesheet #',
                field: 'timesheetNumber',
                sortable: true,
                flex: 2,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
                cellRenderer: 'timesheetNumberRenderer',
            },
            {
                colId: 'JobDate',
                headerName: 'Date',
                field: 'date',
                sortable: true,
                minWidth: 120,
                sort: { direction: 'asc', priority: 0 },
                filter: 'agDateColumnFilter',
                filterParams: DateFilterDefaults,
            },
            {
                colId: 'Start',
                headerName: 'Start',
                field: 'start',
                sortable: false,
                minWidth: 120,
                maxWidth: 120,
                // filter: 'agTextColumnFilter',
                // filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'End',
                headerName: 'End',
                field: 'end',
                sortable: false,
                minWidth: 120,
                maxWidth: 120,
                // filter: 'agTextColumnFilter',
                // filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'ChargeType',
                headerName: 'Charge Type',
                field: 'chargeType',
                sortable: true,
                flex: 1,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'Charge',
                headerName: 'Charge',
                field: 'charge',
                type: 'rightAligned',
                sortable: false,
                minWidth: 120,
                maxWidth: 120,
                cellStyle: (q) => {
                    if ((q.data ?? {}).isBreak) {
                        if (q.data.isPaidBreak) {
                            return { color: 'blue' };
                        }

                        return { color: 'red' };
                    }
                    return {};
                },
                cellClass: (q) => {
                    if ((q.data ?? {}).charge) {
                        if (parseFloat(q.data.charge) > 16.0) {
                            return ['ag-cell-danger'];
                        }
                    }

                    return '';
                },
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'EmployeeName',
                headerName: 'Emp. Name',
                field: 'employeeName',
                sortable: true,
                flex: 1.5,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
                cellRenderer: 'employeeAccountStatusRenderer',
                cellRendererParams: {
                    nameField: 'employeeName',
                    className: 'font-weight-bold',
                },
            },
            {
                colId: 'DispatchLocation',
                headerName: 'Dispatching',
                sortable: true,
                flex: 1.5,
                field: 'dispatchLocation',
                tooltipField: 'dispatchOfficeName',
                filter: 'selectFilter',
                filterParams: dispatchFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: dispatchFilterParams,
            },
            {
                colId: 'CustomerName',
                headerName: 'Customer Name',
                field: 'customerName',
                sortable: true,
                flex: 1.5,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
                valueGetter: (row) => {
                    if (row.data) {
                        const name = `${row.data.customerName}${(row.data.subcontractorName ? ` - ${row.data.subcontractorName}` : '')}`;
                        return name;
                    }

                    return '';
                },
            },
            {
                colId: 'Billable',
                headerName: 'Billable',
                field: 'billable',
                sortable: false,
                minWidth: 120,
                maxWidth: 120,
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: yesNoFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: yesNoFilterParams,
            },
            {
                colId: 'Overlaps',
                headerName: 'Overlaps',
                field: 'overlaps',
                sortable: false,
                minWidth: 120,
                maxWidth: 120,
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: yesNoFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: yesNoFilterParams,
                valueGetter: (row) => {
                    if (row.data) {
                        const val = row.data.overLaps;
                        return val;
                    }

                    return '';
                },
            },
            {
                colId: 'Status',
                headerName: 'Payroll Status',
                field: 'status',
                sortable: true,
                minWidth: 140,
                tooltipField: 'status',
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: payrollStatusFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: payrollStatusFilterParams,
                valueGetter: (row) => {
                    if (row.data) {
                        return row.data.status;
                    }

                    return '';
                },
            },
            {
                colId: 'BillingStatus',
                headerName: 'Billing Status',
                field: 'billingStatus',
                sortable: true,
                minWidth: 140,
                tooltipField: 'status',
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: billingStatusFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: billingStatusFilterParams,
                valueGetter: (row) => {
                    if (row.data) {
                        const isAddStatus = !!row.data.isEmergency || !!row.data.isClientCancelled;
                        let addStatus = '';
                        if (row.data.isEmergency) addStatus += ' - Emergency';
                        if (row.data.isClientCancelled) addStatus += ' - Client Cancel';

                        const status = `${row.data.billingStatus}${(isAddStatus ? addStatus : '')}`;
                        return status;
                    }

                    return '';
                },
            },
            {
                colId: 'WorkOrderNumber',
                headerName: 'Work Order',
                field: 'workOrderNumber',
                sortable: true,
                flex: 1,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'Flag',
                headerName: 'Flag',
                field: 'flag',
                flex: 1,
                minWidth: 65,
                sortable: false,
                tooltipValueGetter: ({ value }) => (value ?? []).join(', '),
                tooltipComponentParams: { color: '#ececec' },
                cellRenderer: 'flagIconRenderer',
                cellRendererParams: {
                    bodyField: 'flag',
                },
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: flagsFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: flagsFilterParams,
            },
            {
                colId: 'id',
                flex: 0,
                minWidth: 75,
                maxWidth: 75,
                headerName: '',
                sortable: false,
                cellRenderer: 'iconRenderer',
                cellRendererParams: {
                    clicked: this.openPayrollCalculation,
                    idField: 'id',
                    iconClass: 'fa-edit payroll-edit-btn',
                },
            },
            {
                colId: 'SelectionPlaceholder',
                headerName: '',
                field: 'selectionPlaceholder',
                sortable: false,
                maxWidth: 75,
                checkboxSelection: true,
            },
        ];

        return defs;
    }

    handleSaveError = (err) => handleFormSaveError(this, err);

    loadTimesheetOptions = debounce((searchString, callback) => {
        httpClient.js(ApiRoutes.typeAheads.pendingPayrollTimesheetsSearch(searchString))
            .then((data) => callback(data));
    }, 250);

    loadChargeResourcesList = async (selectedTimesheet, selectedChargeType) => {
        let billingChargeResources = [];

        if (selectedTimesheet && selectedChargeType && selectedTimesheet.dispatchLocationId > 0) {
            if (selectedChargeType.unitsId == ChargeTypeUnits.Hours) {
                billingChargeResources = await httpClient.js(ApiRoutes.invoice.slideoutStaff(selectedTimesheet.dispatchLocationId));
                billingChargeResources = billingChargeResources.map((x) => ({ label: x.name, value: x.id }));
            } else {
                billingChargeResources = await httpClient.js(ApiRoutes.invoice.slideoutEquipment(selectedTimesheet.dispatchLocationId));
                billingChargeResources = billingChargeResources.map((x) => ({ label: x.description, value: x.id }));
            }
        }

        this.setState({
            chargeStartTime: '', chargeEndTime: '', chargeNotes: '', totalChargeTime: 0, selectedResource: null, selectedTimesheet, selectedChargeType, billingChargeResources,
        });
    };

    openPayrollCalculation = async (id) => {
        if (id) {
            await this.context.setFormOpened(true);
            this.payrollCalculationRef.current.open(id);
        }
    };

    populateState = async () => {
        const dispatchLocations = await httpClient.js(ApiRoutes.typeAheads.dispatchLocations());
        const payrollAllStatuses = await httpClient.js(ApiRoutes.typeAheads.payrollStatuses());
        const billingStatuses = await httpClient.js(ApiRoutes.typeAheads.billingStatuses());
        const flags = await httpClient.js(ApiRoutes.typeAheads.payrollChargeFlags());
        const pendingTimesheets = this.context.tenant.tenantSettings.quirksEnabled ? await httpClient.js(ApiRoutes.typeAheads.pendingPayrollTimesheets()) : [];
        const payrollDispatchLocations = await httpClient.js(ApiRoutes.payrollManagement.payrollChargeLocations());
        const payrollStatuses = payrollAllStatuses.filter((x) => x.value == 1 || x.value == 3 || x.value == 5);
        const yesNo = [
            { label: 'Yes', value: 'true' },
            { label: 'No', value: 'false' },
        ];

        const gridOptions = createGridOptions(this);
        const context = this;

        gridOptions.components = {
            selectFilter: DataGridSelectFilter,
            selectFloatingFilter: DataGridSelectFloatingFilter,
            nameRenderer: LinkCellRenderer,
            iconRenderer: IconCellRenderer,
            variableLinkRenderer: VariableLinkCellRenderer,
            timesheetNumberRenderer: TimesheetNumberCellRenderer,
            flagIconRenderer: FlagIconCellRenderer,
            employeeAccountStatusRenderer: EmployeeAccountStatusRenderer,
        };

        // https://www.ag-grid.com/documentation/javascript/row-selection/#checkbox-selection
        // One column must have "checkboxSelection" set to true.
        // Note: headerCheckboxSelection not available with infinite scroll.
        gridOptions.rowSelection = 'multiple';

        gridOptions.postProcessData = transformRowData;
        gridOptions.onRowSelected = this.onRowSelected;

        gridOptions.columnDefs = this.getColumnDefs({
            dispatchLocations, payrollStatuses, billingStatuses, flags, yesNo,
        });
        const { enablePrevailingWageCountyGroups = false } = this.context.tenant.tenantSettings ?? {};
        // this allows row colors to be set
        gridOptions.rowClassRules = {
            'ag-row-danger': (params) => (params.data ?? {}).isEmergency === true,
            'ag-row-warning': (params) => ((params.data ?? {}).overLaps === 'Yes' || (params.data ?? {}).timeError === true) && (params.data ?? {}).isEmergency === false,
            'ag-row-info': (params) => (params.data ?? {}).isClientCancelled === true && (params.data ?? {}).isEmergency === false,
            'ag-row-success': (params) => {
                const matches = ((params.data ?? {}).flag ?? []).filter((f) => f === PayrollFlags.ChargeTypeThresholdExceeded || f === PayrollFlags.BreakThresholdExceeded);

                return matches.length > 0;
            },
            'ag-row-dst': (params) => ((params.data ?? {}).flag ?? []).includes(PayrollFlags.DaylightSavingTime),
            'ag-row-nopsschedule': (params) => {
                const targetFlag = enablePrevailingWageCountyGroups
                    ? PayrollFlags.NoCountyGroupsOnPWContract
                    : PayrollFlags.NoPayScheduleOnPWContract;
                const matches = ((params.data ?? {}).flag ?? []).filter((f) => f === targetFlag);
                return matches.length > 0;
            },
        };

        // Loading Spinner
        gridOptions.useLoading = true;
        gridOptions.loadingTemplate = '<span className="ag-overlay-loading-center "><i class="fa-spin fa fa-md fa-circle-notch"></i> Loading ...</span>';

        gridOptions.onGridReady = (params) => {
            const start = moment(moment().subtract(30, 'days')).format(
                'YYYY-MM-DD',
            );
            const end = moment(moment().subtract(1, 'days')).format(
                'YYYY-MM-DD',
            );

            const filterInstance = gridOptions.api.getFilterInstance('JobDate');
            filterInstance.setModel({
                type: 'inRange',
                dateFrom: start,
                dateTo: end,
            });
            gridOptions.api.onFilterChanged();

            params.api.setDatasource(context.state.dataSource);
            context.setState({ gridApi: params.api });
        };

        const dataSource = createDataSource(ApiRoutes.payrollManagement.search(), gridOptions);
        const payrollChargeDate = moment(new Date()).format(MomentDateFormat.DateOnlyUTCHyphenated);

        this.setState({
            dispatchLocations,
            payrollStatuses,
            billingStatuses,
            flags,
            yesNo,
            loading: false,
            gridOptions,
            dataSource,
            isRejectingPayroll: false,
            billingChargeResources: [],
            selectedTimesheet: null,
            selectedChargeType: null,
            pendingTimesheets,
            paymentChargeTypes: [],
            payrollDispatchLocations,
            payrollChargeDate,
            selectedPayrollChargeType: null,
            selectedPayrollEmployee: null,
            selectedPayrollDispatchLocation: null,
            payrollCharges: null,
            payrollChargeValue: 0,
        });
    };

    rejectPayroll = async ({ selectedRowRejectionNotes }, adminRejecting) => {
        const { rowsSelected } = this.state;

        if (selectedRowRejectionNotes.length === 0 || !selectedRowRejectionNotes.trim()) return;
        this.setState({ isRejectingPayroll: true });

        // call reject
        if (rowsSelected) {
            const model = {
                payrollId: rowsSelected[0].id,
                note: selectedRowRejectionNotes,
            };

            const endPoint = adminRejecting == true ? ApiRoutes.payrollManagement.adminReject() : ApiRoutes.payrollManagement.reject();
            const response = await httpClient.post(endPoint, model)
                .catch(this.handleSaveError);

            if (response) {
                toasty.success('Payroll: Timesheet Rejected');
            }
        }

        this.state.gridOptions.refresh();

        this.setState({
            selectedRow: null,
            showRejectPayrollModal: false,
            selectedRowRejectionNotes: '',
            isRejectingPayroll: false,
            adminRejecting: false,
        });
    };

    toggleAddChargeModal = () => {
        const { showAddChargeModal } = this.state;

        this.setState({
            showAddChargeModal: !showAddChargeModal,
            billingChargeResources: [],
            selectedTimesheet: null,
            selectedChargeType: null,
            selectedResource: null,
            chargeStartTime: 0,
            chargeEndTime: 0,
            totalChargeTime: 0,
            chargeNotes: '',
        });
    };

    toggleAddPayrollChargeModal = () => {
        const { showAddPayrollChargeModal } = this.state;
        this.setState({
            showAddChargeModal: false,
            showAddPayrollChargeModal: !showAddPayrollChargeModal,
            isAddingCharge: false,
            payrollCharges: null,
            selectedPayrollChargeType: null,
            selectedPayrollEmployee: null,
            selectedPayrollDispatchLocation: null,
            payrollChargeDate: null,
            dispatchLocationEmployees: null,
            payrollChargeValue: 0,
        });
    };

    toggleRejectPayrollModal = async () => {
        const { rowsSelected } = this.state;

        const adminRequired = await httpClient.get(ApiRoutes.payrollManagement.needsAdminReject(rowsSelected[0].id));

        // if admin not needed just show regular
        if (adminRequired == false) {
            this.showRejectPayrollModal(false);
        }

        // if admin needed and user is not admin
        if (adminRequired == true && !this.context?.user?.isAdmin) {
            toasty.info('An admin needs to reject this timesheet due to it being processed already');
            return;
        }

        // if admin needed and is an admin
        if (adminRequired == true && this.context?.user?.isAdmin) {
            this.showRejectPayrollModal(true);
        }
    };

    showRejectPayrollModal = (adminRejecting) => {
        Modal.show({
            title: 'Payroll: Reject Timesheet',
            onSubmit: (values) => this.rejectPayroll(values, adminRejecting),
            SaveButtonProps: {
                label: 'Reject',
                savingLabel: 'Rejecting',
                icon: faUndo,
                color: 'danger',
            },
            body: () => (
                <>
                    {(adminRejecting
                        ? (
                            <small
                                style={{ marginBottom: '20px' }}
                                className="form-text text-danger"
                            >
                                This administrative action will delete any processed payroll or billing records, and reset the timesheet.  Only administrators will be allowed to adjust and re-process this timesheet.  Please take note of Payroll and Billing implications.
                            </small>
                        )
                        : (
                            <FormText style={{ marginBottom: '20px' }}>
                                This action will reject all charges on the associated timesheet from payroll review and remove the timesheet from billing review.
                            </FormText>
                        )
                    )}

                    <Textarea
                        label="Notes"
                        id="selectedRowRejectionNotes"
                        name="selectedRowRejectionNotes"
                        required
                        placeholder="Enter notes regarding the rejection."
                        maxLength="500"
                        rows="5"
                        error="Notes are required"
                    />
                </>
            ),
        });
    };

    showProcessModal = () => {
        const { dispatchLocations } = this.state;
        const { tenant } = this.context;
        const { tenantSettings } = tenant;

        Modal.show({
            title: 'Process Payroll',
            onSubmit: (values) => this.onProcessClick(values),
            SaveButtonProps: {
                label: 'Process',
                savingLabel: 'Processing',
            },
            body: () => (
                <>
                    <ValidatedSelect
                        placeholder="Select Dispatching"
                        id="selectedDispatchLocation"
                        name="selectedDispatchLocation"
                        label="Dispatching"
                        isClearable={false}
                        options={dispatchLocations}
                        required
                        error="You must select a dispatch office"
                    />

                    <Input
                        label={tenantSettings.payPeriodTypeId === PayPeriodTypes.Weekly ? 'Week Of' : 'Period Of'}
                        id="weekOf"
                        name="selectedWeekOf"
                        type="date"
                        required
                    />
                </>
            ),
        });
    };

    onDispatchLocationChanged = async (event) => {
        let payrollCharges = [];
        let dispatchLocationEmployees = [];
        if (event?.value) {
            payrollCharges = await httpClient.js(ApiRoutes.payrollManagement.payrollCharges(event.value));
            dispatchLocationEmployees = await httpClient.js(ApiRoutes.employees.byDispatchLocation(event.value));
            if (!payrollCharges || payrollCharges?.length == 0) {
                toasty.info('No payroll charges found for this dispatch location.');
            }
        }

        this.setState({
            selectedPayrollDispatchLocation: event,
            payrollCharges,
            selectedPayrollChargeType: null,
            selectedPayrollEmployee: null,
            dispatchLocationEmployees,
        });
    };

    render() {
        const {
            rowData,
            gridOptions,
            rowsSelected,
            payrollDispatchLocations,
            isProcessing,
            showAddChargeModal,
            isAddingCharge,
            pendingTimesheets,
            paymentChargeTypes,
            billingChargeResources,
            selectedResource,
            selectedChargeType,
            chargeStartTime,
            chargeEndTime,
            totalChargeTime,
            chargeNotes,
            isApproving,
            payrollChargeDate,
            payrollCharges,
            selectedPayrollChargeType,
            selectedPayrollEmployee,
            selectedPayrollDispatchLocation,
            dispatchLocationEmployees,
            payrollChargeValue,
            showAddPayrollChargeModal,
        } = this.state;
        // TODO: Eliminate inline styles.
        return (
            <CommonContext.Consumer>
                {
                    (value) => {
                        const { tenantSettings } = ((value ?? {}).tenant ?? {});

                        if (this.state.loading || !tenantSettings) return (<Progress />);

                        if (!tenantSettings.payrollEnabled) return <NotAuthorizedPage />;
                        return (
                            <PageWrap>
                                <PageHeading>
                                    <FontAwesomeIcon
                                        icon={faMoneyCheck}
                                        className="mr-2 text-muted"
                                    />
                                    <span>Payroll: Charges</span>
                                    <span style={{ float: 'right', position: 'relative', top: '-5px' }}>

                                        {rowsSelected.length == 1 && (
                                            <>
                                                <Button
                                                    size="sm"
                                                    color="success"
                                                    style={{ marginRight: '10px' }}
                                                    onClick={() => this.onApproveButtonClick()}
                                                    disabled={
                                                        rowsSelected.length === 0
                                                        || isApproving
                                                    }
                                                >
                                                    Approve Selected
                                                </Button>

                                                <Button
                                                    size="sm"
                                                    color="danger"
                                                    style={{ marginRight: '40px' }}
                                                    onClick={() => this.toggleRejectPayrollModal()}
                                                    disabled={rowsSelected.length !== 1}
                                                >
                                                    Reject Selected
                                                </Button>
                                            </>
                                        )}

                                        {rowsSelected.length > 1 && (
                                            <Button
                                                size="sm"
                                                color="success"
                                                style={{ marginRight: '40px' }}
                                                onClick={() => this.onApproveButtonClick()}
                                                disabled={
                                                    rowsSelected.length === 0
                                                    || isApproving
                                                }
                                            >
                                                Approve Selected
                                            </Button>
                                        )}

                                        <Button
                                            size="sm"
                                            color="success"
                                            style={{ marginRight: '10px' }}
                                            onClick={() => this.toggleAddChargeModal()}
                                            disabled={isProcessing}
                                        >
                                            Add Charge
                                        </Button>
                                        {!tenantSettings.quirksEnabled && (
                                            <Button
                                                size="sm"
                                                color="success"
                                                style={{ marginRight: '10px' }}
                                                onClick={() => this.toggleAddPayrollChargeModal()}
                                                disabled={isProcessing}
                                            >
                                                Add Payroll Charge
                                            </Button>
                                        )}

                                        <Button
                                            size="sm"
                                            style={{ marginRight: '10px' }}
                                            color="success"
                                            onClick={() => this.showProcessModal()}
                                        >
                                            Process
                                        </Button>

                                        {/*    <Button */}
                                        {/*        size="sm" */}
                                        {/*        style={{ marginRight: "10px" }} */}
                                        {/*        color="danger" */}
                                        {/*        onClick={() => this.onRejectButtonClick()} */}
                                        {/*        disabled={rowsSelected.length === 0} */}
                                        {/*    > */}
                                        {/*        Reject Selected */}
                                        {/* </Button> */}

                                        {/*    <Button */}
                                        {/*        size="sm" */}
                                        {/*        color="success" */}
                                        {/*        onClick={() => this.onAcceptButtonClick()} */}
                                        {/*        disabled={rowsSelected.length === 0} */}
                                        {/*    > */}
                                        {/*        Approve Selected */}
                                        {/* </Button> */}
                                    </span>
                                </PageHeading>
                                <DataGridToolbar
                                    entity="Payroll"
                                    gridApi={this.state.gridApi}
                                    dataSource={this.state.dataSource}
                                    hideAdd
                                    hideExcelButton
                                    gridOptions={this.state.gridOptions}
                                    serverExport={{ apiPath: ApiRoutes.payrollManagement.excelExport(), filePrefix: 'PayrollChargesSearch' }}
                                />
                                <DataGrid
                                    domLayout="normal"
                                    rowData={rowData}
                                    gridOptions={gridOptions}
                                    gridStatus={this.state.gridStatus}
                                />
                                <PayrollCalculationSlideout
                                    ref={this.payrollCalculationRef}
                                    show={this.state.payrollCalculationOpen}
                                    toggleShow={(open) => this.setState({ payrollCalculationOpen: open })}
                                    onClose={this.onPayrollCalculationClosed}
                                />



                                <ModalForm
                                    isOpen={showAddPayrollChargeModal}
                                    onSubmit={() => this.onAddPayrollCharge()}
                                    toggle={() => this.toggleAddPayrollChargeModal()}
                                    title="Add Payroll Charge"
                                    SaveButtonProps={{
                                        label: 'Add',
                                        saving: isAddingCharge,
                                    }}
                                >
                                    <Input
                                        label="Date"
                                        required
                                        id="timesheetDate"
                                        name="timesheet.date"
                                        className="rounded-left"
                                        value={
                                            moment(payrollChargeDate).format(
                                                MomentDateFormat.DateOnlyUTCHyphenated,
                                            ) ?? ''
                                        }
                                        onChange={(ev) => {
                                            const val = ev?.target?.value;
                                            this.setState(
                                                (state) => (
                                                    (state.payrollChargeDate = moment(
                                                        val,
                                                    ).format(
                                                        MomentDateFormat.DateOnlyUTCHyphenated,
                                                    )),
                                                    state
                                                ),
                                            );
                                        }}
                                        type="date"
                                        error="Date is required."
                                    />

                                    <ValidatedSelect
                                        id="dispatchOfficeLocation"
                                        name="dispatchOfficeLocationId"
                                        label="Dispatching Office"
                                        options={payrollDispatchLocations}
                                        value={selectedPayrollDispatchLocation}
                                        onChange={this.onDispatchLocationChanged}
                                        required
                                        error="A dispatch selection is required."
                                    />

                                    <ValidatedSelect
                                        menuPosition="fixed"
                                        maxMenuHeight={150}
                                        isClearable={false}
                                        label="Charge"
                                        name="selectedPayrollChargeType"
                                        required
                                        error="A charge type must be selected"
                                        isDisabled={false}
                                        value={selectedPayrollChargeType}
                                        options={
                                            payrollCharges ?? []
                                        }
                                        onChange={(event) => this.setState({ selectedPayrollChargeType: event })}
                                        getOptionLabel={(o) => o.description}
                                        getOptionValue={(o) => o.chargeTypeId}
                                    />

                                    <ValidatedSelect
                                        menuPosition="fixed"
                                        maxMenuHeight={150}
                                        isClearable={false}
                                        label="Employee"
                                        name="selectedPayrollEmployee"
                                        required
                                        error="An employee selection is required"
                                        isDisabled={false}
                                        value={selectedPayrollEmployee}
                                        options={
                                            dispatchLocationEmployees ?? []
                                        }
                                        onChange={(event) => this.setState({ selectedPayrollEmployee: event })}
                                        getOptionLabel={(o) => o.fullName}
                                        getOptionValue={(o) => o.id}
                                    />

                                    <NumericInput
                                        label="Quantity/Hours"
                                        required
                                        error="The quantity/hours for this charge is required"
                                        min="0"
                                        preventNegative
                                        step={0.5}
                                        name="payrollChargeValue"
                                        value={
                                            payrollChargeValue ?? ''
                                        }
                                        onChange={(evt) => {
                                            const { value } = evt.target;

                                            this.setState({
                                                payrollChargeValue: value,
                                            });
                                        }}
                                        onBlur={(evt) => {
                                            const { value } = evt.target;

                                            this.setState({
                                                payrollChargeValue: parseFloat(
                                                    value,
                                                ).toFixed(
                                                    2,
                                                ),
                                            });
                                        }}
                                    />
                                </ModalForm>

                                <ModalForm
                                    isOpen={showAddChargeModal}
                                    onSubmit={() => this.onAddCharge()}
                                    toggle={() => this.toggleAddChargeModal()}
                                    title="Add Charge"
                                    SaveButtonProps={{
                                        label: 'Add',
                                        saving: isAddingCharge,
                                    }}
                                >
                                    {this.context.tenant.tenantSettings.quirksEnabled ? (
                                        <ValidatedSelect
                                            placeholder="Select Timesheet"
                                            id="selectedChargeTimesheet"
                                            name="selectedChargeTimesheet"
                                            label="Timesheet"
                                            isClearable={false}
                                            options={pendingTimesheets}
                                            onChange={this.onSelectedTimesheetChanged}
                                            value={this.state.selectedTimesheet}
                                            required
                                            error="A timesheet must be selected"
                                        />
                                    ) : (
                                        <ValidatedSelect
                                            component={AsyncSelect}
                                            label="Timesheet"
                                            placeholder="Select Timesheet"
                                            id="selectedChargeTimesheet"
                                            name="selectedChargeTimesheet"
                                            isClearable={false}
                                            loadOptions={this.loadTimesheetOptions}
                                            onChange={this.onSelectedTimesheetChanged}
                                            value={this.state.selectedTimesheet}
                                            required
                                            error="A timesheet must be selected"
                                        />
                                    )}

                                    <ValidatedSelect
                                        placeholder="Select Charge Type"
                                        id="selectedChargeType"
                                        name="selectedChargeType"
                                        label="Charge Type"
                                        isClearable={false}
                                        value={(paymentChargeTypes ?? []).find((x) => x.value === selectedChargeType?.value) ?? ''}
                                        options={paymentChargeTypes}
                                        onChange={this.onSelectedChargeTypeChanged}
                                        error="A charge type selection is required"
                                        required
                                    />

                                    <ValidatedSelect
                                        placeholder="Select Resource"
                                        id="selectedChargeResource"
                                        name="selectedChargeResource"
                                        label={selectedChargeType && selectedChargeType.unitsId == ChargeTypeUnits.Hours ? 'Employee' : 'Equipment'}
                                        isClearable={false}
                                        value={(billingChargeResources ?? []).find((x) => x.value === selectedResource?.value) ?? ''}
                                        options={billingChargeResources}
                                        onChange={this.onSelectedResourceChanged}
                                        required
                                        error="A resource selection is required"
                                    />

                                    {!!selectedChargeType && selectedChargeType.unitsId == ChargeTypeUnits.Hours && (
                                        <>
                                            <FormGroup
                                                className="payrollstart"
                                                style={{ width: '60px' }}
                                            >
                                                <FormLabel
                                                    htmlFor="chargeStartTime"
                                                    label="Start"
                                                    required
                                                />
                                                <TimePicker
                                                    required
                                                    id="chargeStartTime"
                                                    name="chargeStartTime"
                                                    increment={
                                                        Number(selectedChargeType?.increment) > 0
                                                            ? Number(selectedChargeType.increment)
                                                            : 0.25
                                                    }
                                                    value={chargeStartTime}
                                                    onChange={(ev) => this.onTimeEntryStartChanged(ev)}
                                                />
                                            </FormGroup>
                                            <FormGroup
                                                className="payrollend"
                                                style={{ width: '60px' }}
                                            >
                                                <FormLabel
                                                    htmlFor="chargeEndTime"
                                                    label="End"
                                                    required
                                                />
                                                <TimePicker
                                                    required
                                                    id="chargeEndTime"
                                                    name="chargeEndTime"
                                                    increment={
                                                        Number(selectedChargeType?.increment) > 0
                                                            ? Number(selectedChargeType.increment)
                                                            : 0.25
                                                    }
                                                    value={chargeEndTime}
                                                    onChange={(ev) => this.onTimeEntryEndChanged(ev)}
                                                />
                                            </FormGroup>
                                        </>
                                    )}
                                    <Input
                                        label="Charge"
                                        id="chargeValue"
                                        name="chargeValue"
                                        value={totalChargeTime}
                                        disabled={!!selectedChargeType && selectedChargeType.unitsId == ChargeTypeUnits.Hours}
                                        onChange={(ev) => this.onChargeValueChanged(ev)}
                                        required
                                    />
                                    <Textarea
                                        label="Notes"
                                        id="chargeNotes"
                                        name="chargeNotes"
                                        value={chargeNotes}
                                        onChange={(ev) => this.onChargeNotesChanged(ev)}
                                        placeholder="Enter notes"
                                        maxLength="500"
                                        rows="5"
                                        required
                                    />
                                </ModalForm>
                            </PageWrap>
                        );
                    }
                }
            </CommonContext.Consumer>
        );
    }
}

export default PayrollManagementIndex;
